// ============================================================
// TransitionRate — replaces app-transition-rate
// Loads CSV 10085.csv, aggregates Upper Primary / Secondary / Higher Secondary transition rates
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  parseCsvToObjects,
  sumField,
  getMngTypeOnUserType,
} from '../../shared/utils';
import { RootState } from '../../../../store';
import ChartPanelWrapper from '../../../../shared/graph/graphPanelWrapper';
import SharedFilter from '../../../../shared/filter/sharedFilter';
import { FilterState } from '../../../../shared/filter/filterTypes';
import '../../../../shared/filter/sharedFilter.scss';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../dashboard.scss';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CSV_URL = 'https://d3ijhv7dn0xr3b.cloudfront.net/10085.csv';
const YEARS = ['2020-21', '2021-22', '2022-23'];
const COLORS = ['#05a34a', '#5970ca', '#fbbc06'];

interface TransitionRow {
  Acad_yr: string;
  Upr: string;
  Sec: string;
  HSec: string;
}

const TransitionRate: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = String(s.districtId ?? '');
  const edu_district_id = String(s.edu_district_id ?? '');
  const blockId = String(s.blockId ?? '');

  const [overallData, setOverallData] = useState<any[]>([]);
  const [finalList, setFinalList] = useState<TransitionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filterState, setFilterState] = useState<FilterState | null>(null);

  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      complete: (results) => {
        let rows = parseCsvToObjects(results.data as string[][]);
        if (usertypeid === '24' && ['5', '6'].includes(emisUsertype1)) {
          rows = rows.filter((x) => String(x.manage_id) === '2');
        }
        setOverallData(rows);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, [usertypeid, emisUsertype1]);

  useEffect(() => {
    if (overallData.length === 0) return;

    // NOTE: For TransitionRate, the category type filter controls which
    // transition datasets (bars) appear in the chart — NOT the raw data filtering.
    const geoType = getMngTypeOnUserType(usertypeid, emisUsertype1);
    let filtered = overallData;

    // For state level, filter district_id == 0 (state aggregate rows)
    if (geoType === 'state') {
      filtered = overallData.filter((x) => String(x.district_id) === '0');
    } else if (geoType === 'district') {
      filtered = overallData.filter(
        (x) => String(x.district_id) === districtId
      );
    } else if (geoType === 'edu_district') {
      filtered = overallData.filter(
        (x) => String(x.edu_dist_id) === edu_district_id
      );
    } else if (geoType === 'block') {
      filtered = overallData.filter((x) => String(x.block_id) === blockId);
    }

    const rows: TransitionRow[] = YEARS.map((yr) => {
      const yearData = filtered.filter((x) => x.Acad_yr === yr);
      console.log('yearData', yearData);
      return {
        Acad_yr: yr,
        Upr: sumField(
          yearData.filter((x) => x.Trans_type === 'Upr'),
          'Upr_Transition_Rate'
        ).toFixed(2),
        Sec: sumField(
          yearData.filter((x) => x.Trans_type === 'Sec'),
          'Upr_Transition_Rate'
        ).toFixed(2),
        HSec: sumField(
          yearData.filter((x) => x.Trans_type === 'HSec'),
          'Upr_Transition_Rate'
        ).toFixed(2),
      };
    });
    queueMicrotask(() => setFinalList(rows));
  }, [
    overallData,
    usertypeid,
    emisUsertype1,
    districtId,
    edu_district_id,
    blockId,
  ]);

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  // Build chart datasets based on filter state — only show checked category transitions
  const datasets = [];
  // Primary to Middle = primary school category
  if (!filterState || filterState.primarySchoolChk) {
    datasets.push({
      label: 'Primary to Middle (%)',
      data: finalList.map((r) => parseFloat(r.Upr)),
      backgroundColor: COLORS[0],
    });
  }
  // Middle to High = middle school category
  if (!filterState || filterState.middleSchoolChk) {
    datasets.push({
      label: 'Middle to High (%)',
      data: finalList.map((r) => parseFloat(r.Sec)),
      backgroundColor: COLORS[1],
    });
  }
  // High to Higher Secondary = high school category
  if (!filterState || filterState.highSchoolChk) {
    datasets.push({
      label: 'High to Higher Secondary (%)',
      data: finalList.map((r) => parseFloat(r.HSec)),
      backgroundColor: COLORS[2],
    });
  }

  const chartData = {
    labels: YEARS,
    datasets,
  };

  const options = {
    responsive: true,
    plugins: { legend: { position: 'top' as const } },
    scales: { y: { title: { display: true, text: 'Transition Rate (%)' } } },
  };

  const chartContent = (
    <>
      <div className="d-flex justify-content-center align-items-center">
        <p
          className="text-muted text-center mt-2"
          style={{ fontSize: '0.8rem' }}
        >
          (Hover on the Graph to view Numbers)
        </p>
      </div>
      <Bar data={chartData} options={options} />
    </>
  );

  const gridContent = (
    <div className="comparative-grid mt-3">
      <div className="mb-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="global-search-input"
        />
      </div>
      <DataTable
        value={finalList}
        sortMode="single"
        size="small"
        stripedRows
        emptyMessage="No data found"
        globalFilter={globalFilter}
        globalFilterFields={['Acad_yr', 'Upr', 'Sec', 'HSec']}
      >
        <Column field="Acad_yr" header="Academic Year" sortable />
        {(!filterState || filterState.primarySchoolChk) && (
          <Column field="Upr" header="Upper Primary Transition (%)" sortable />
        )}
        {(!filterState || filterState.middleSchoolChk) && (
          <Column field="Sec" header="Secondary Transition (%)" sortable />
        )}
        {(!filterState || filterState.highSchoolChk) && (
          <Column
            field="HSec"
            header="Higher Secondary Transition (%)"
            sortable
          />
        )}
      </DataTable>
    </div>
  );

  return (
    <div className="chart-with-filter">
      <SharedFilter showFilters={['3']} onFilterChange={handleFilterChange} />
      <ChartPanelWrapper
        title=""
        chartContent={chartContent}
        gridContent={gridContent}
        loading={loading}
      />
    </div>
  );
};

export default TransitionRate;
